import { LogManager } from "./logging/LogManager";
import { StringUtil } from "./StringUtil";

export enum RegisteredRouteType {
    Login = 'login',
    Home = 'home',
    SessionExpiration = 'sessionExpiration',
    SignInRequired = 'signInRequired',
    NoLicensesAvailable = 'noLicensesAvailable',
    PasswordReset = 'passwordReset',
}

interface RegisteredRouteDef {
    path: string;
    isDisallowedForward?: boolean;
    hidePageHeader?: boolean;
    hideMainMenu?: boolean;
}

const log = LogManager.getLogger('core.RegisteredRoutes');

export class RegisteredRoutes {
    /** A default map of routes that would be used in any given web application */
    private static readonly defaultRouteRegister: Map<RegisteredRouteType, RegisteredRouteDef> = new Map([
        [RegisteredRouteType.Login,
            { path: 'common/Login', isDisallowedForward: true, hidePageHeader: true, hideMainMenu: true }],
        [RegisteredRouteType.Home,
            { path: 'common/home/Home', isDisallowedForward: false, hidePageHeader: false, hideMainMenu: false }],
        [RegisteredRouteType.SessionExpiration,
            { path: 'common/SessionExpired', isDisallowedForward: true, hidePageHeader: false, hideMainMenu: true }],
        [RegisteredRouteType.SignInRequired,
            { path: 'common/SignInNeeded', isDisallowedForward: true, hidePageHeader: false, hideMainMenu: true }],
        [RegisteredRouteType.NoLicensesAvailable,
            { path: 'common/NoLicensesAvailable', isDisallowedForward: true, hidePageHeader: false, hideMainMenu: true }],
        [RegisteredRouteType.PasswordReset,
            { path: 'common/ResetPassword', isDisallowedForward: false, hidePageHeader: true, hideMainMenu: true }],
    ]);

    /** A map of registered routes that is the home for any specialized routes that a web application might want
     * to define. Can be used to override a defaultStaticRoute or, in a pinch, define your own that route that
     * may not be suitable as a general route for any given web application.
     */
    private static registeredRoutes: Map<RegisteredRouteType | string, RegisteredRouteDef> = new Map<string, RegisteredRouteDef>();

    /** Registers a specialized route for your web application. You may override a pre-existing RegisteredRouteType or
     * custom-define a new route needed for your web application.
     */
    public static register(type: RegisteredRouteType | string, def: RegisteredRouteDef) {
        if (StringUtil.isEmptyString(type) || StringUtil.isEmptyString(def?.path)) {
            log.error("Cannot register a route without a proper type or path.");
            return;
        }
        this.registeredRoutes.set(type, def);
    }

    /** Determines if the provided `path` disallows forward navigation.
     * @return false by default, or false if the provided `path` has not been registered.
     */
    public static disallowsForwardNavigationTo(path: string): boolean {
        const route: RegisteredRouteDef = this.getRegisteredRouteForPath(path);
        return route?.isDisallowedForward ?? false;
    }

    /** Determines is the registered page should display a page header.
     * @return true by default, or true if the provided path is not registered.
     */
    public static hasPageHeader(path: string): boolean {
        const route: RegisteredRouteDef = this.getRegisteredRouteForPath(path);
        return route?.hidePageHeader !== true;
    }

    /**
     * Determines if the registered page should display the main menu in the header.
     *
     * Expectation is that the registered route is used as the primary layout of the Router.
     * This setting will not alter the menu state if the registered route is nested or showing
     * in an overlay.
     * @return true by default, or true if the provided path is not registered.
     */
    public static hasMainMenu(path: string): boolean {
        const route: RegisteredRouteDef = this.getRegisteredRouteForPath(path);
        return route?.hideMainMenu !== true;
    }

    private static getRegisteredRouteForPath(path: string): RegisteredRouteDef | null {
        if (StringUtil.isEmptyString(path))
            return null;
        return this.findRouteInMap(path, this.registeredRoutes) ?? this.findRouteInMap(path, this.defaultRouteRegister) ?? null;
    }

    private static findRouteInMap(path: string, routeMap: Map<string, RegisteredRouteDef>): RegisteredRouteDef | null {
        if (StringUtil.isEmptyString(path))
            return null;
        for (const value of routeMap.values()) {
            if (value.path.toLowerCase().startsWith(path.toLowerCase()))
                return value;
        }
        return null;
    }

    /** Returns the path for the loginRoute. If no specialized route has been registered
     * for your web application a default value is provided.
     */
    public static get loginRoute(): string {
        return this.getRegisteredRouteByType(RegisteredRouteType.Login);
    }

    /** Returns the path for the homeRoute. If no specialized route has been registered
     * for your web application a default value is provided.
     */
    public static get homeRoute(): string {
        return this.getRegisteredRouteByType(RegisteredRouteType.Home);
    }

    /** Returns the path for the sessionExpirationRoute. If no specialized route has been registered
     * for your web application a default value is provided.
     */
    public static get sessionExpirationRoute(): string {
        return this.getRegisteredRouteByType(RegisteredRouteType.SessionExpiration);
    }

    /** Returns the path for the signInRequiredRoute. If no specialized route has been registered
     * for your web application a default value is provided.
     */
    public static get signInRequiredRoute(): string {
        return this.getRegisteredRouteByType(RegisteredRouteType.SignInRequired);
    }

    /** Returns the path for the noLicensesAvailableRoute. If no specialized route has been registered
     * for your web application a default value is provided.
     */
    public static get noLicensesAvailableRoute(): string {
        return this.getRegisteredRouteByType(RegisteredRouteType.NoLicensesAvailable);
    }

    /** Returns the path for the passwordResetRoute. If no specialized route has been registered
     * for your web application a default value is provided.
     */
    public static get passwordResetRoute(): string {
        return this.getRegisteredRouteByType(RegisteredRouteType.PasswordReset);
    }

    private static getRegisteredRouteByType(type: RegisteredRouteType) {
        const registeredPath = this.registeredRoutes.get(type)?.path
        return StringUtil.isEmptyString(registeredPath) ? this.defaultRouteRegister.get(type).path : registeredPath;
    }

    /** Get a route. Used for getting custom-defined routes that
     * have been defined within the scope of some specific web application.
     *
     * Use the specific getters provided in `RegisteredRoutes` for
     * general purpose web application routes, such as login or session expiration.
     */
    public static getRegisteredRoute(type: string): string | null {
        if (StringUtil.isEmptyString(type))
            return null;
        return this.registeredRoutes.get(type)?.path;
    }
}
